/**
 * This module defines the Rename action and its bindings to an `ItemsScope`.
 */
import * as React from 'react';
import { renameItem as defaultRenameItem } from '../../../../operations/renameItem';
import {
  IDecorateWithSelectedItemsProps,
  IDecorateWithExactlyOneItemProps,
  withSelectedItems,
  withExactlyOneItem
} from '../../../decorators/selectors';
import { renameActionControl, IRenameWizardProps } from '../../../../controls/renameActionControl';
import { WithItemCache, IItemCacheStateProps } from '../../../withItemCache/WithItemCache';
import { WizardAction, IWizardActionStateProps } from '../../../wizardAction/WizardAction';
import { fileNameFacet } from '../../../../facets/ItemFacets';
import { getItemActionEngagement } from '../../../../models/telemetry/getItemEngagement';

export interface IItemRenameActionProps {
  itemKey: string;
  renameWizard: React.ComponentType<IRenameWizardProps>;
  renameItem?: typeof defaultRenameItem;
}

export class ItemRenameAction extends React.PureComponent<IItemRenameActionProps> {
  public render(): JSX.Element {
    const {
      itemKey,
      renameWizard,
      renameItem = defaultRenameItem
    } = this.props;

    return (
      <WithItemCache>
        { ({ itemCache, dispatch }: IItemCacheStateProps) => {

          if (!itemKey) {
            return <WizardAction isAvailable={ false } />;
          }

          let isAvailable = true;

          let FinalRenameWizard = renameWizard;

          const fileName = itemCache.demandItemFacet(fileNameFacet, itemKey);

          const oldName = fileName && fileName.name || '';

          const controlHandler = itemCache.demandItemFacet(renameActionControl, itemKey);

          const {
            isAvailable: itemIsAvailable,
            renameWizard: ItemRenameWizard,
            validator,
            lockedWarningMessage,
            title
          } = renameActionControl.evaluate(controlHandler)(itemCache, {
            itemKey: itemKey,
            isAvailable: false,
            renameWizard: renameWizard
          });

          if (!itemIsAvailable) {
            isAvailable = false;
          }

          FinalRenameWizard = ItemRenameWizard;

          const onRename = async (props: { name: string; }) => {
            await dispatch(renameItem({
              items: {
                [itemKey]: {
                  names: {
                    [oldName]: {
                      newName: props.name
                    }
                  }
                }
              }
            }));
          };

          return (
            <WizardAction
              isAvailable={ isAvailable }
              { ...getItemActionEngagement(itemCache, {
                itemKey,
                engagement: {
                  name: 'Rename',
                  isIntentional: true
                }
              }) }
            >
              { (wizardProps: IWizardActionStateProps) => {
                return (
                  <FinalRenameWizard
                    { ...wizardProps }
                    isAvailable={ isAvailable && !!itemKey && !!fileName }
                    itemKey={ itemKey }
                    onRename={ onRename }
                    oldName={ oldName }
                    lockedWarningMessage={ lockedWarningMessage }
                    fileExtensionText={ fileName && fileName.extension }
                    title={ title }
                    validator={ validator }
                  />
                );
              } }
            </WizardAction>
          );
        } }
      </WithItemCache>
    );
  }
}

export type IRenameActionProps = IDecorateWithSelectedItemsProps<IDecorateWithExactlyOneItemProps<IItemRenameActionProps>>;

export const RenameAction: React.ComponentType<IRenameActionProps> = withSelectedItems()(withExactlyOneItem()(ItemRenameAction));
